import { Component, DestroyRef, EventEmitter, inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { timer } from 'rxjs';
import { ApiHttpService } from "../../../services/api-http.service";
import {
  CognitoAuthenticatedUser,
  GeneralConfirmationModalData,
  GeneralConfirmationModalResult,
  GOOGLE_LINK_ERROR_MESSAGE_KEYS,
  UserDto
} from "../../../models";
import { GoogleLinkService } from "../../../services/google-link.service";
import { DataStorageService } from "../../../services/data-storage.service";
import { Observable } from "rxjs";
import { AccountService } from "../../../services/account.service";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import {
  GeneralConfirmationModalComponent
} from "../../../components/general-confirmation-modal/general-confirmation-modal.component";
import { Router } from "@angular/router";
import { DialogService } from "../../../services/dialog.service";
import { environment } from '../../../../environments/environment';
import { countryNameKey } from "../../../utils";

@Component({
  selector: 'cpf-account-preview',
  templateUrl: './account-preview.component.html',
  styleUrls: ['./account-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AccountPreviewComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  readonly isDev = !environment.production;
  grantVoucherStatus: 'idle' | 'success' | 'error' = 'idle';

  googleLinkAction: 'idle' | 'connecting' | 'disconnecting' = 'idle';
  googleLinkMessage: { kind: 'success' | 'error', key: string } | null = null;

  public avatar?: string;
  public user?: UserDto;
  public composedUserAddress = '';
  public ibanLastFour = '';
  cognitoUser$: Observable<CognitoAuthenticatedUser | null>;
  langs = ['en', 'de', 'fr', 'it'];
  currentLang = 'de';
  @Output() editAccountClicked = new EventEmitter<void>();
  @Output() changePasswordClicked = new EventEmitter<void>();
  @Output() changeIbanClicked = new EventEmitter<void>();
  @Output() contactUsClicked = new EventEmitter<void>();
  @Output() vouchersClicked = new EventEmitter<void>();

  constructor(private apiHttpService: ApiHttpService,
              private dataStorageService: DataStorageService,
              private accountService: AccountService,
              private dialog: MatDialog,
              private translate: TranslateService,
              private router: Router,
              private dialogService: DialogService,
              private googleLinkService: GoogleLinkService,) {
    this.user = dataStorageService.user;
    this.cognitoUser$ = accountService.user$;

    this.currentLang = localStorage.getItem('lang') || this.translate.getCurrentLang() || 'de';

    this.translate.onLangChange.subscribe(() => {
      this.currentLang = localStorage.getItem('lang') || this.translate.getCurrentLang() || 'de';
    })
  }

  async ngOnInit() {
    this.avatar = (await this.apiHttpService.getUrlForProfilePicture()).Location;

    this.checkUserName();

    this.composeUserAddress();

    if (this.user?.iban) {
      this.ibanLastFour = this.user.iban.replace(/\s/g, '').slice(-4);
    }

    this.googleLinkMessage = this.readGoogleCallbackMessage();
  }

  checkUserName() {
    if (!this.user) {
      return
    }

    if (!this.user.firstName) {
      this.user.firstName = this.dataStorageService.selectedData?.firstname
    }

    if (!this.user.lastName) {
      this.user.lastName = this.dataStorageService.selectedData?.lastname
    }

    if (!this.user.phoneNumber) {
      this.user.phoneNumber = this.dataStorageService.selectedData?.phone_number
    }

    if (!this.user.parent_birthdate) {
      this.user.parent_birthdate = this.dataStorageService.selectedData?.parent_birthdate
    }

    if (!this.user.street) {
      this.user.street = this.dataStorageService.selectedData?.street
    }

    if (!this.user.houseNumber) {
      this.user.houseNumber = this.dataStorageService.selectedData?.house_number
    }

    if (!this.user.city) {
      this.user.city = this.dataStorageService.selectedData?.city
    }

    if (!this.user.zip) {
      this.user.zip = this.dataStorageService.selectedData?.zip
    }

    if (!this.user.country) {
      this.user.country = this.dataStorageService.selectedData?.country
    }
  }

  composeUserAddress() {
    const user = this.user;
    if (!user) {
      return;
    }
    const { street, houseNumber, zip, city, country } = user;
    const address = []

    if (street) {
      address.push(street);
    }

    if (houseNumber) {
      address.push(houseNumber);
    }

    if (city) {
      address.push(city);
    }

    if (zip) {
      address.push(zip);
    }

    if (country) {
      // CloudInsurance stores an ISO code, so `CH` is turned into a name.
      const key = countryNameKey(country);
      address.push(key ? this.translate.instant(key) : country);
    }

    this.composedUserAddress = address.join(', ');
  }

  logout() {
    this.translate.get(['account-preview.modals.logout.title', 'account-preview.modals.logout.message']).subscribe(translations => {
      const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, {
        data: {
          title: translations['account-preview.modals.logout.title'],
          message: translations['account-preview.modals.logout.message'],
          confirmButton: GeneralConfirmationModalResult.Confirm,
          cancelButton: GeneralConfirmationModalResult.Cancel,
        } as GeneralConfirmationModalData
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === GeneralConfirmationModalResult.Confirm) {
          this.dialog.closeAll();
          this.accountService.logout();
        }
      });
    });
  }

  setLang(lang: string) {
    localStorage.setItem('lang', lang);
    localStorage.setItem('explicitly-set-lang', 'true');
    this.translate.use(lang);
  }

  navigateEdit() {
    if (this.editAccountClicked.observed) {
      this.editAccountClicked.emit();
      return;
    }
    this.router.navigate(['calingo', 'account', 'edit']);
  }

  navigateChangePassword() {
    if (this.changePasswordClicked.observed) {
      this.changePasswordClicked.emit();
      return;
    }
    this.router.navigate(['calingo', 'account', 'change-password']);
  }

  navigateChangeIban() {
    if (this.changeIbanClicked.observed) {
      this.changeIbanClicked.emit();
      return;
    }
    this.router.navigate(['calingo', 'account', 'change-iban']);
  }

  navigateContactUs() {
    if (this.contactUsClicked.observed) {
      this.contactUsClicked.emit();
      return;
    }
    this.router.navigate(['calingo', 'account', 'contact-us']);
  }

  navigateVouchers() {
    if (this.vouchersClicked.observed) {
      this.vouchersClicked.emit();
      return;
    }
    this.router.navigate(['calingo', 'account', 'vouchers']);
  }

  get isGoogleLinked(): boolean {
    // Cognito's own spelling is `Google`; don't make the card depend on the casing.
    return !!this.user?.linkedProviders?.some(provider => provider.toLowerCase() === 'google');
  }

  /**
   * Hands the browser over to Google. The consent URL has to be asked for
   * instead of linked to directly, because the endpoint identifies the account
   * to link by the Authorization header.
   */
  async connectGoogle(): Promise<void> {
    if (this.googleLinkAction !== 'idle') {
      return;
    }

    this.googleLinkAction = 'connecting';
    this.googleLinkMessage = null;

    try {
      const { authUrl } = await this.apiHttpService.startGoogleLink();

      if (!authUrl) {
        throw new Error('No authUrl in the /link/google/start response');
      }

      // The busy state is deliberately left standing: the page is on its way out.
      window.location.href = authUrl;
    } catch (error) {
      console.error('Could not start the Google account link', error);
      this.googleLinkAction = 'idle';
      this.googleLinkMessage = {
        kind: 'error',
        key: 'account-preview.connected-accounts.messages.errors.start-failed'
      };
    }
  }

  disconnectGoogle() {
    if (this.googleLinkAction !== 'idle') {
      return;
    }

    this.translate.get([
      'account-preview.modals.disconnect-google.title',
      'account-preview.modals.disconnect-google.message'
    ]).subscribe(translations => {
      const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, {
        data: {
          title: translations['account-preview.modals.disconnect-google.title'],
          message: translations['account-preview.modals.disconnect-google.message'],
          confirmButton: GeneralConfirmationModalResult.Confirm,
          cancelButton: GeneralConfirmationModalResult.Cancel,
        } as GeneralConfirmationModalData
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === GeneralConfirmationModalResult.Confirm) {
          this.unlinkGoogle();
        }
      });
    });
  }

  /** Turns the callback the user came back on into the banner above the card. */
  private readGoogleCallbackMessage(): { kind: 'success' | 'error', key: string } | null {
    const result = this.googleLinkService.consumeCallbackResult();

    if (!result) {
      return null;
    }

    if (result.status === 'connected' || result.status === 'linked') {
      return { kind: 'success', key: 'account-preview.connected-accounts.messages.connected' };
    }

    // Codes that share one message still have to be told apart in a support
    // ticket, so the exact code the backend sent goes to the console.
    console.warn('Google account link refused:', result.rawReason ?? '(no reason given)');

    // Reasons the backend does not name, or names with a code we don't know, get
    // the catch-all copy.
    const messageKey = result.reason
      ? GOOGLE_LINK_ERROR_MESSAGE_KEYS[result.reason]
      : 'generic';

    return {
      kind: 'error',
      key: `account-preview.connected-accounts.messages.errors.${messageKey}`
    };
  }

  private async unlinkGoogle(): Promise<void> {
    this.googleLinkAction = 'disconnecting';
    this.googleLinkMessage = null;

    const unlinked = await this.apiHttpService.unlinkGoogle();

    if (unlinked) {
      if (unlinked.staleToken) {
        console.warn('The unlink was made with a token issued before it; refreshing before reading the link state.');
      }

      // The refresh is awaited on purpose. An ID token from before the unlink
      // still carries the link, and `getCurrentUser` with it would put the
      // connection straight back onto the profile.
      await this.apiHttpService.forceRefresh();
      await this.refreshUser();
    }

    this.googleLinkAction = 'idle';
    this.googleLinkMessage = unlinked
      ? { kind: 'success', key: 'account-preview.connected-accounts.messages.disconnected' }
      : { kind: 'error', key: 'account-preview.connected-accounts.messages.errors.unlink-failed' };
  }

  private async refreshUser(): Promise<void> {
    const user = await this.apiHttpService.getCurrentUser();

    // `getCurrentUser` swallows its own errors and resolves undefined; keeping
    // the user we already have beats blanking the whole screen.
    if (!user) {
      return;
    }

    this.user = user;
    this.dataStorageService.user = user;
    this.checkUserName();
    this.composeUserAddress();
  }

  async grantMeikoVoucher(): Promise<void> {
    this.grantVoucherStatus = 'idle';
    try {
      const ok = await this.apiHttpService.grantMeikoVoucher();
      this.grantVoucherStatus = ok ? 'success' : 'error';
    } catch {
      this.grantVoucherStatus = 'error';
    }
    timer(3000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.grantVoucherStatus = 'idle';
    });
  }
}
